import { useState, useEffect, useRef } from 'react';
import { MapPin, Building2, MonitorSmartphone, X, ArrowLeft, Link2, Wrench, FileSpreadsheet } from 'lucide-react';
import { api } from '../lib/api.jsx';
import { useTableSort } from '../lib/useTableSort.jsx';
import { useDashboard } from '../views/dashboardContext.jsx';
import { hasPermission } from '../lib/permissions.jsx';
import { fmtMachineType, fmtOnlineStatus, fmtOsName, onlineSortValue } from '../lib/format.jsx';
import { MachineTypeBadge } from '../components/MachineTypeBadge.jsx';
import { ComitClientPicker } from '../components/ComitClientPicker.jsx';
import { MaintenanceModal } from '../components/MaintenanceModal.jsx';
import { useComitConfig } from '../lib/comit.jsx';

const inputCls =
  'w-full px-3 py-2.5 bg-sidebar border border-rule rounded-lg text-fg-2 text-[13px] outline-none box-border';
const labelCls = 'block text-[11px] text-muted-2 mb-1.5';
const sectionCls = 'bg-card border border-rule rounded-2xl px-5 py-5 mb-4';
const sTitleCls = 'text-sm font-bold text-fg-2 mb-3.5 flex items-center justify-between';
const btnPrimary =
  'px-4 py-2 rounded-lg border-none cursor-pointer font-semibold text-xs bg-gradient-to-br from-blue-500 to-violet-500 text-white disabled:opacity-60';
const btnSecondary =
  'px-4 py-2 rounded-lg cursor-pointer font-medium text-xs bg-transparent border border-rule text-muted hover:bg-card-hi';
const btnSmall =
  'px-3 py-1 rounded-md cursor-pointer font-medium text-[11px] bg-transparent border border-rule text-muted hover:bg-card-hi';

// Le formulaire d'édition est le contrat de PUT /api/clients/{id} : tout champ
// omis ici serait écrasé côté serveur (l'update remplace la fiche entière).
const formFromClient = (c) => ({
  name: c.name,
  contactEmail: c.contactEmail || '',
  contactPhone: c.contactPhone || '',
  address: c.address || '',
  notes: c.notes || '',
  comitClientId: c.comitClientId || 0,
  comitClientName: c.comitClientName || '',
});

const archBadge = (arch) => {
  const isArm = (arch || '').toLowerCase().includes('arm');
  return isArm ? 'bg-orange-500/15 text-orange-400' : 'bg-blue-500/15 text-blue-400';
};

// Même convention que la jauge CPU/RAM/disque du Dashboard (rouge >=95%, ambre >=80%).
const PBar = (p) => (
  <div className="w-full h-2 bg-rule rounded overflow-hidden">
    <div
      className="h-full rounded"
      style={{
        width: `${Math.min(p, 100)}%`,
        background: p >= 95 ? '#ef4444' : p >= 80 ? '#f59e0b' : '#22c55e',
        transition: 'width .5s',
      }}
    />
  </div>
);
const barCell = (p) => (
  <div className="flex items-center gap-1.5">
    <div className="flex-1">{PBar(p || 0)}</div>
    <span className="text-[11px] text-muted font-mono text-right" style={{ minWidth: 32 }}>
      {(p || 0).toFixed(0)}%
    </span>
  </div>
);
const alertBadge = (n) =>
  n > 0 ? (
    <span
      className="px-2 py-0.5 rounded text-[10px] font-semibold"
      style={{
        color: n >= 3 ? '#fca5a5' : '#fcd34d',
        background: n >= 3 ? 'rgba(239,68,68,.12)' : 'rgba(245,158,11,.12)',
      }}
    >
      {n}
    </span>
  ) : (
    <span className="text-muted-3 text-[10px]">—</span>
  );

export function ClientDetail({ clientId, onBack, onSelectSite, onSelectDevice, onShowAlerts }) {
  const comit = useComitConfig();
  const { perms } = useDashboard();
  const canManageAlerts = hasPermission(perms, 'ManageAlerts');
  const canViewDevices = hasPermission(perms, 'ViewDevices');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  // Fenêtre de maintenance active par site — un seul appel groupé plutôt que
  // de faire porter la requête au composant de la carte, pour ne pas
  // multiplier les GET à chaque re-rendu.
  const [siteMaintenance, setSiteMaintenance] = useState({});
  const [maintenanceSiteId, setMaintenanceSiteId] = useState(null);
  const [data, setData] = useState(null);
  const [devices, setDevices] = useState([]);
  const [devSearch, setDevSearch] = useState('');
  // Filtre site appliqué à la table d'appareils (clic sur une carte de site)
  const [siteFilter, setSiteFilter] = useState(null);
  const devTableRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [showSiteForm, setShowSiteForm] = useState(false);
  const { sortRows, headerProps, arrow } = useTableSort('computerName', 'asc');
  const [siteForm, setSiteForm] = useState({ name: '', location: '' });
  const [siteSaving, setSiteSaving] = useState(false);
  const [provisioningSiteId, setProvisioningSiteId] = useState(null);
  const [provLabel, setProvLabel] = useState('');
  const [provSaving, setProvSaving] = useState(false);
  const [provResult, setProvResult] = useState(null);
  const [provCopied, setProvCopied] = useState(false);
  const [editingSiteId, setEditingSiteId] = useState(null);
  const [siteEditForm, setSiteEditForm] = useState({ name: '', location: '' });
  const [siteEditSaving, setSiteEditSaving] = useState(false);

  const load = async () => {
    const r = await api.get('/clients/' + clientId);
    if (r) {
      setData(r);
      // Mise à jour fonctionnelle obligatoire : `load` est aussi appelé par un
      // setInterval qui capture le `editForm` du montage (toujours null). Avec
      // un `if (!editForm)` sur la variable capturée, le formulaire en cours
      // d'édition serait réécrit depuis le serveur toutes les 15 s.
      setEditForm((prev) => prev ?? formFromClient(r.client));
    }
    setLoading(false);
  };

  useEffect(() => {
    setSiteFilter(null);
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, [clientId]);

  const loadSiteMaintenance = () => {
    const siteIds = (data?.sites || []).map((s) => s.id);
    if (siteIds.length === 0) return;
    Promise.all(siteIds.map((id) => api.get('/platform/maintenance-windows?siteId=' + id))).then((results) => {
      const map = {};
      siteIds.forEach((id, i) => {
        map[id] = results[i]?.active || null;
      });
      setSiteMaintenance(map);
    });
  };

  useEffect(() => {
    loadSiteMaintenance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Clic sur une carte de site : (dé)filtre la table d'appareils et la ramène
  // dans le viewport — sinon le filtre s'applique hors écran sans feedback.
  const toggleSiteFilter = (siteId) => {
    setSiteFilter((cur) => {
      const next = cur === siteId ? null : siteId;
      if (next && devTableRef.current)
        setTimeout(() => devTableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
      return next;
    });
    onSelectSite?.(siteId);
  };

  useEffect(() => {
    const loadDevs = async () => {
      const params = '?clientId=' + clientId + (devSearch ? '&search=' + encodeURIComponent(devSearch) : '');
      const r = await api.get('/devices' + params);
      if (r) setDevices(r);
    };
    loadDevs();
  }, [clientId, devSearch]);

  const handleEditSave = async () => {
    if (!editForm.name.trim()) return;
    setEditSaving(true);
    await api.put('/clients/' + clientId, editForm);
    setEditing(false);
    setEditSaving(false);
    setEditForm(null);
    load();
  };

  const handleCreateSite = async () => {
    if (!siteForm.name.trim()) return;
    setSiteSaving(true);
    await api.post('/sites', { name: siteForm.name, location: siteForm.location, clientId });
    setSiteForm({ name: '', location: '' });
    setShowSiteForm(false);
    setSiteSaving(false);
    load();
  };

  const startEditSite = (s) => {
    closeProvision();
    setEditingSiteId((cur) => (cur === s.id ? null : s.id));
    setSiteEditForm({ name: s.name, location: s.location || '' });
  };

  const handleEditSiteSave = async (siteId) => {
    if (!siteEditForm.name.trim()) return;
    setSiteEditSaving(true);
    await api.put('/sites/' + siteId, {
      name: siteEditForm.name,
      location: siteEditForm.location,
      clientId,
    });
    setEditingSiteId(null);
    setSiteEditSaving(false);
    load();
  };

  const handleProvision = async (siteId) => {
    setProvSaving(true);
    const r = await api.post('/provisions', { clientId, siteId, label: provLabel });
    if (r) setProvResult(r);
    setProvSaving(false);
  };

  const closeProvision = () => {
    setProvisioningSiteId(null);
    setProvLabel('');
    setProvResult(null);
    setProvCopied(false);
  };

  const copyProvCode = (code) => {
    navigator.clipboard.writeText(code);
    setProvCopied(true);
    setTimeout(() => setProvCopied(false), 2000);
  };

  // Même pattern authentifié-fetch-puis-blob que `exportCsv` de AuditPage : le
  // token passe par `api.fetch` (header Authorization), et le nom de fichier
  // est repris de `Content-Disposition` plutôt que reconstruit côté client —
  // c'est le serveur qui connaît le nom du client tel qu'utilisé côté export.
  const exportSpecs = async () => {
    setExporting(true);
    setExportError('');
    try {
      const res = await api.fetch(`/clients/${clientId}/export/specs`);
      if (!res.ok) throw new Error('export failed');
      const disposition = res.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="?([^"; ]+)"?/i);
      const fileName = match ? match[1] : `specs-${clientId}.xlsx`;
      const blob = await res.blob();
      const dl = document.createElement('a');
      dl.href = URL.createObjectURL(blob);
      dl.download = fileName;
      document.body.appendChild(dl);
      dl.click();
      dl.remove();
      URL.revokeObjectURL(dl.href);
    } catch {
      setExportError("Échec de l'export. Réessayez.");
    } finally {
      setExporting(false);
    }
  };

  if (loading) return <div className="text-muted-2 p-10">Chargement...</div>;
  if (!data) return <div className="text-crit p-10">Client introuvable</div>;

  const c = data.client;
  const sites = data.sites || [];
  const filteredSite = siteFilter ? sites.find((s) => s.id === siteFilter) : null;
  const shownDevices = siteFilter ? devices.filter((d) => d.siteId === siteFilter) : devices;

  return (
    <div>
      <div className="flex items-center gap-3 mb-5">
        <button
          onClick={onBack}
          className="bg-rule border-none rounded-lg px-3.5 py-2 text-muted cursor-pointer text-xs font-semibold hover:bg-rule-2 inline-flex items-center gap-1.5"
        >
          <ArrowLeft size={14} strokeWidth={1.75} /> Retour
        </button>
        <div className="flex-1">
          <h1 className="text-[22px] font-bold text-fg">{c.name}</h1>
          <div className="text-xs text-muted-2 mt-0.5">
            {[c.contactEmail, c.contactPhone].filter(Boolean).join(' • ')}
          </div>
        </div>
        <button
          onClick={() => {
            if (editing) setEditing(false);
            else {
              setEditForm(formFromClient(c));
              setEditing(true);
            }
          }}
          className={btnSmall}
        >
          {editing ? 'Annuler' : 'Modifier le client'}
        </button>
      </div>

      {editing && editForm && (
        <div className="bg-card border border-blue-500 rounded-2xl px-5 py-5 mb-4">
          <h3 className="text-sm font-semibold text-fg-2 mb-3.5">Modifier le client</h3>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className={labelCls}>Nom *</label>
              <input
                className={inputCls}
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              />
            </div>
            <div>
              <label className={labelCls}>Email</label>
              <input
                className={inputCls}
                value={editForm.contactEmail}
                onChange={(e) => setEditForm({ ...editForm, contactEmail: e.target.value })}
              />
            </div>
            <div>
              <label className={labelCls}>Téléphone</label>
              <input
                className={inputCls}
                value={editForm.contactPhone}
                onChange={(e) => setEditForm({ ...editForm, contactPhone: e.target.value })}
              />
            </div>
            <div>
              <label className={labelCls}>Adresse</label>
              <input
                className={inputCls}
                value={editForm.address}
                onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
              />
            </div>
          </div>
          {comit.enabled && (
            <div className="mb-3.5">
              <label className={labelCls}>Client ComIT lié</label>
              <ComitClientPicker
                value={editForm.comitClientId ? { id: editForm.comitClientId, name: editForm.comitClientName } : null}
                onChange={(next) =>
                  setEditForm({
                    ...editForm,
                    comitClientId: next ? next.id : 0,
                    comitClientName: next ? next.name : '',
                  })
                }
              />
              <div className="text-[11px] text-muted-2 mt-1.5">
                Pré-remplit le client sur l'écran ComIT « créer un bon de travail » ouvert depuis une alerte
                de ce client.
              </div>
            </div>
          )}
          <div className="mb-3.5">
            <label className={labelCls}>Notes</label>
            <textarea
              className={`${inputCls} min-h-[50px] resize-y`}
              value={editForm.notes}
              onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
            />
          </div>
          <div className="flex gap-2.5">
            <button
              className={btnPrimary}
              onClick={handleEditSave}
              disabled={editSaving || !editForm.name.trim()}
            >
              {editSaving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
            <button className={btnSecondary} onClick={() => setEditing(false)}>
              Annuler
            </button>
          </div>
        </div>
      )}

      {!editing && (c.address || comit.enabled) && (
        <div className="text-xs text-muted mb-4 flex items-center gap-4 flex-wrap">
          {c.address && (
            <span className="inline-flex items-center gap-1.5">
              <MapPin size={13} strokeWidth={1.75} /> {c.address}
            </span>
          )}
          {comit.enabled && (
            <span
              className={`inline-flex items-center gap-1.5 ${c.comitClientId ? 'text-muted' : 'text-muted-2'}`}
              title="Client ComIT lié — utilisé pour créer un bon de travail depuis une alerte"
            >
              <Link2 size={13} strokeWidth={1.75} />
              ComIT :{' '}
              {c.comitClientId ? (
                <span className="text-fg-2">{c.comitClientName || '#' + c.comitClientId}</span>
              ) : (
                'non lié'
              )}
            </span>
          )}
        </div>
      )}

      <div className={sectionCls}>
        <div className={sTitleCls}>
          <span className="inline-flex items-center gap-2">
            <Building2 size={16} strokeWidth={1.75} /> Sites ({sites.length})
          </span>
          <button onClick={() => setShowSiteForm(!showSiteForm)} className={btnSmall}>
            {showSiteForm ? 'Annuler' : '+ Ajouter un site'}
          </button>
        </div>

        {showSiteForm && (
          <div className="bg-sidebar border border-blue-500 rounded-xl px-4 py-3 mb-3">
            <div className="grid grid-cols-[1fr_1fr_auto] gap-2.5 items-end">
              <div>
                <label className={labelCls}>Nom du site *</label>
                <input
                  className={inputCls}
                  value={siteForm.name}
                  onChange={(e) => setSiteForm({ ...siteForm, name: e.target.value })}
                  placeholder="ex: Bureau principal"
                  onKeyDown={(e) => e.key === 'Enter' && handleCreateSite()}
                />
              </div>
              <div>
                <label className={labelCls}>Emplacement</label>
                <input
                  className={inputCls}
                  value={siteForm.location}
                  onChange={(e) => setSiteForm({ ...siteForm, location: e.target.value })}
                  placeholder="ex: Montréal"
                  onKeyDown={(e) => e.key === 'Enter' && handleCreateSite()}
                />
              </div>
              <button
                className={btnPrimary}
                onClick={handleCreateSite}
                disabled={siteSaving || !siteForm.name.trim()}
              >
                {siteSaving ? 'Création...' : 'Créer'}
              </button>
            </div>
          </div>
        )}

        {sites.length === 0 && !showSiteForm ? (
          <div className="text-muted-2 text-xs">Aucun site</div>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-3">
            {sites.map((s) => {
              const isProv = provisioningSiteId === s.id;
              const isFiltered = siteFilter === s.id;
              return (
                <div
                  key={s.id}
                  onClick={() => toggleSiteFilter(s.id)}
                  title={isFiltered ? 'Retirer le filtre' : 'Filtrer les appareils sur ce site'}
                  className={`bg-sidebar border rounded-xl px-4 py-3.5 cursor-pointer transition-colors ${
                    isProv
                      ? 'border-violet-500'
                      : isFiltered
                        ? 'border-blue-500 bg-blue-500/5'
                        : 'border-rule hover:border-blue-500'
                  }`}
                >
                  {/* Nom + pastille d'état sur une ligne, boutons sur la ligne
                      suivante : la carte est trop étroite pour les trois boutons
                      à côté du nom, qui se cassait en deux lignes. */}
                  <div className="flex justify-between items-center gap-3 mb-1.5">
                    <div className="font-bold text-fg text-sm min-w-0 break-words">{s.name}</div>
                    <div
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{
                        background:
                          s.status === 'critical'
                            ? '#ef4444'
                            : s.status === 'warning'
                              ? '#f59e0b'
                              : '#22c55e',
                      }}
                    />
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          startEditSite(s);
                        }}
                        className={`px-2.5 py-0.5 rounded font-medium text-[10.5px] whitespace-nowrap cursor-pointer border ${
                          editingSiteId === s.id
                            ? 'border-blue-500 bg-blue-500/10 text-blue-400'
                            : 'border-rule-2 bg-transparent text-muted-2'
                        }`}
                      >
                        {editingSiteId === s.id ? 'Annuler' : 'Modifier'}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isProv) closeProvision();
                          else {
                            closeProvision();
                            setEditingSiteId(null);
                            setProvisioningSiteId(s.id);
                          }
                        }}
                        className={`px-2.5 py-0.5 rounded font-medium text-[10.5px] whitespace-nowrap cursor-pointer border ${
                          isProv
                            ? 'border-violet-500 bg-violet-500/10 text-violet-400'
                            : 'border-rule-2 bg-transparent text-muted-2'
                        }`}
                      >
                        {isProv ? 'Fermer' : 'Provisionner'}
                      </button>
                      {canManageAlerts && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setMaintenanceSiteId(s.id);
                          }}
                          title="Suspendre temporairement les alertes et notifications sur ce site"
                          className={`px-2.5 py-0.5 rounded font-medium text-[10.5px] whitespace-nowrap cursor-pointer border ${
                            siteMaintenance[s.id]
                              ? 'border-blue-500 bg-blue-500/10 text-blue-400'
                              : 'border-rule-2 bg-transparent text-muted-2'
                          }`}
                        >
                          <Wrench size={10} strokeWidth={1.75} className="inline -mt-px mr-1" />
                          Maintenance
                        </button>
                      )}
                    </div>
                  {s.location && <div className="text-[11px] text-muted-2 mb-1.5">{s.location}</div>}
                  <div className="flex gap-4 text-[11px] text-muted items-center">
                    <span>
                      {s.onlineCount}/{s.devicesCount} en ligne
                    </span>
                    {s.alertsCount > 0 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onShowAlerts?.(s.id);
                        }}
                        title="Voir les alertes de ce site"
                        className="bg-transparent border-none p-0 text-crit text-[11px] cursor-pointer underline decoration-dotted underline-offset-2 hover:text-red-400"
                      >
                        {s.alertsCount} alerte{s.alertsCount > 1 ? 's' : ''}
                      </button>
                    )}
                    {siteMaintenance[s.id] && (
                      <span className="text-blue-400 text-[10px] font-semibold inline-flex items-center gap-1">
                        <Wrench size={10} strokeWidth={1.75} /> En maintenance
                      </span>
                    )}
                    {isFiltered && (
                      <span className="ml-auto text-[10px] text-blue-400 font-semibold">Filtré</span>
                    )}
                  </div>

                  {editingSiteId === s.id && (
                    <div className="mt-3 pt-3 border-t border-rule" onClick={(e) => e.stopPropagation()}>
                      <div className="grid grid-cols-[1fr_1fr] gap-2 mb-2">
                        <div>
                          <label className="block text-[10px] text-muted-2 mb-1">Nom du site *</label>
                          <input
                            className={`${inputCls} text-xs py-1.5 px-2.5`}
                            value={siteEditForm.name}
                            onChange={(e) => setSiteEditForm({ ...siteEditForm, name: e.target.value })}
                            onKeyDown={(e) => e.key === 'Enter' && handleEditSiteSave(s.id)}
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] text-muted-2 mb-1">Emplacement</label>
                          <input
                            className={`${inputCls} text-xs py-1.5 px-2.5`}
                            value={siteEditForm.location}
                            onChange={(e) => setSiteEditForm({ ...siteEditForm, location: e.target.value })}
                            onKeyDown={(e) => e.key === 'Enter' && handleEditSiteSave(s.id)}
                          />
                        </div>
                      </div>
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => setEditingSiteId(null)}
                          className="px-3 py-1 rounded-md border border-rule-2 bg-transparent text-muted text-[11px] cursor-pointer"
                        >
                          Annuler
                        </button>
                        <button
                          onClick={() => handleEditSiteSave(s.id)}
                          disabled={siteEditSaving || !siteEditForm.name.trim()}
                          className={`${btnPrimary} text-[11px] px-3.5 py-1.5`}
                        >
                          {siteEditSaving ? 'Enregistrement...' : 'Enregistrer'}
                        </button>
                      </div>
                    </div>
                  )}

                  {isProv && (
                    <div className="mt-3 pt-3 border-t border-rule" onClick={(e) => e.stopPropagation()}>
                      {!provResult ? (
                        <div className="flex gap-2 items-end">
                          <div className="flex-1">
                            <label className="block text-[10px] text-muted-2 mb-1">Libellé (optionnel)</label>
                            <input
                              className={`${inputCls} text-xs py-1.5 px-2.5`}
                              value={provLabel}
                              onChange={(e) => setProvLabel(e.target.value)}
                              placeholder="ex: PC-Accueil"
                              onKeyDown={(e) => e.key === 'Enter' && handleProvision(s.id)}
                            />
                          </div>
                          <button
                            onClick={() => handleProvision(s.id)}
                            disabled={provSaving}
                            className={`${btnPrimary} text-[11px] px-3.5 py-1.5 whitespace-nowrap`}
                          >
                            {provSaving ? '...' : 'Générer'}
                          </button>
                        </div>
                      ) : (
                        <div className="text-center">
                          <div className="text-[10px] text-muted-2 mb-1.5">Code de provisionnement :</div>
                          <div className="font-mono text-[22px] font-bold text-violet-400 tracking-[3px] mb-2">
                            {provResult.code}
                          </div>
                          <div className="flex gap-2 justify-center">
                            <button
                              onClick={() => copyProvCode(provResult.code)}
                              className={`px-3.5 py-1 rounded-md border bg-transparent text-[11px] cursor-pointer ${
                                provCopied ? 'border-ok text-emerald-300' : 'border-rule-2 text-muted'
                              }`}
                            >
                              {provCopied ? 'Copié !' : 'Copier le code'}
                            </button>
                            <button
                              onClick={() => {
                                setProvResult(null);
                                setProvLabel('');
                              }}
                              className="px-3.5 py-1 rounded-md border border-rule-2 bg-transparent text-muted text-[11px] cursor-pointer"
                            >
                              Nouveau code
                            </button>
                          </div>
                          <div className="text-[10px] text-muted-3 mt-1.5">
                            Expire le{' '}
                            {new Date(provResult.expiresAt).toLocaleDateString('fr-FR', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {maintenanceSiteId && (
        <MaintenanceModal
          siteId={maintenanceSiteId}
          label={sites.find((s) => s.id === maintenanceSiteId)?.name}
          onClose={(changed) => {
            setMaintenanceSiteId(null);
            if (changed) loadSiteMaintenance();
          }}
        />
      )}

      <div className={sectionCls} ref={devTableRef}>
        <div className="flex items-center justify-between mb-3.5 gap-2 flex-wrap">
          <div className="text-sm font-bold text-fg-2 flex items-center gap-2">
            <MonitorSmartphone size={16} strokeWidth={1.75} /> Appareils
            {filteredSite && (
              <span className="inline-flex items-center gap-1.5 bg-blue-500/15 border border-blue-500/30 text-blue-400 rounded px-2 py-0.5 text-[11px] font-medium">
                Site : {filteredSite.name}
                <button
                  onClick={() => setSiteFilter(null)}
                  title="Retirer le filtre"
                  className="bg-transparent border-none text-blue-400 cursor-pointer p-0 inline-flex items-center hover:text-blue-300"
                >
                  <X size={11} strokeWidth={2.5} />
                </button>
              </span>
            )}
            <span className="text-[11px] text-muted-2 font-normal">{shownDevices.length}</span>
          </div>
          {canViewDevices && (
            <div className="flex items-center gap-2">
              {exportError && <span className="text-[11px] text-crit">{exportError}</span>}
              <button
                onClick={exportSpecs}
                disabled={exporting || devices.length === 0}
                className={`${btnSecondary} inline-flex items-center gap-1.5`}
                title="Exporter les fiches techniques de tous les appareils du client"
              >
                <FileSpreadsheet size={13} strokeWidth={1.75} />
                {exporting ? 'Export…' : 'Exporter (Excel)'}
              </button>
            </div>
          )}
        </div>
        <input
          className={`${inputCls} mb-3`}
          placeholder="Rechercher un appareil (nom, IP, OS)..."
          value={devSearch}
          onChange={(e) => setDevSearch(e.target.value)}
        />

        {shownDevices.length === 0 ? (
          <div className="text-muted-2 text-xs">
            {siteFilter ? 'Aucun appareil sur ce site' : 'Aucun appareil'}
          </div>
        ) : (
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr>
                {[
                  { key: 'computerName', label: 'Nom' },
                  { key: 'siteName', label: 'Site' },
                  { key: 'machineType', label: 'Type' },
                  { key: 'osArchitecture', label: 'Arch' },
                  { key: 'osName', label: 'OS' },
                  { key: 'ipAddress', label: 'IP' },
                  { key: 'agentVersion', label: 'Agent' },
                  { key: 'isOnline', label: 'Statut' },
                  { key: 'alertCount', label: 'Alertes' },
                  { key: 'cpuPercent', label: 'CPU' },
                  { key: 'memoryPercent', label: 'RAM' },
                  { key: 'diskPercent', label: 'Disque' },
                  { key: null, label: '' },
                ].map((c, i) => (
                  <th
                    key={c.label || 'spacer' + i}
                    {...headerProps(c.key)}
                    className="px-3 py-2 text-left text-muted-2 font-medium text-[11px] uppercase border-b border-rule whitespace-nowrap"
                  >
                    {c.label}
                    {arrow(c.key)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortRows(shownDevices, (d, key) => {
                if (key === 'computerName') return d.label || d.computerName;
                if (key === 'isOnline') return onlineSortValue(d.isOnline, d.lastHeartbeat);
                // Trier sur l'étiquette affichée, pas sur la valeur brute anglaise.
                if (key === 'machineType') return fmtMachineType(d.machineType);
                return d[key];
              }).map((d) => (
                <tr
                  key={d.id}
                  onClick={() => onSelectDevice(d.id)}
                  className="cursor-pointer transition-colors hover:bg-blue-500/5"
                >
                  <td className="px-3 py-2 text-fg-2 border-b border-sidebar font-mono">
                    <div className="font-semibold text-fg-2">{d.label || d.computerName}</div>
                    {d.label && <div className="text-[10px] text-muted-2 mt-0.5">{d.computerName}</div>}
                  </td>
                  <td className="px-3 py-2 text-fg-2 border-b border-sidebar">{d.siteName}</td>
                  <td className="px-3 py-2 border-b border-sidebar">
                    <MachineTypeBadge machineType={d.machineType} platform={d.platform} />
                  </td>
                  <td className="px-3 py-2 border-b border-sidebar">
                    {d.osArchitecture ? (
                      <span
                        className={`px-2 py-0.5 rounded text-[10px] font-semibold font-mono ${archBadge(d.osArchitecture)}`}
                      >
                        {d.osArchitecture}
                      </span>
                    ) : (
                      <span className="text-muted-3 text-[10px]">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-fg-2 border-b border-sidebar text-[11px]" title={d.osName || undefined}>
                    {fmtOsName(d.osName)}
                  </td>
                  <td className="px-3 py-2 text-fg-2 border-b border-sidebar font-mono text-[11px]">
                    {d.ipAddress}
                  </td>
                  <td className="px-3 py-2 text-fg-2 border-b border-sidebar font-mono text-[11px]">
                    {d.agentVersion || '—'}
                  </td>
                  <td className="px-3 py-2 border-b border-sidebar">
                    <div className="flex items-center gap-1.5">
                      <div className={`w-1.5 h-1.5 rounded-full ${d.isOnline ? 'bg-ok' : 'bg-crit'}`} />
                      <span
                        className={`text-[11px] ${d.isOnline ? 'text-emerald-300' : 'text-red-300'}`}
                        title={!d.isOnline && d.lastHeartbeat ? `Dernier heartbeat : ${new Date(d.lastHeartbeat).toLocaleString('fr-FR')}` : undefined}
                      >
                        {fmtOnlineStatus(d.isOnline, d.lastHeartbeat)}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-2 border-b border-sidebar text-center">{alertBadge(d.alertCount)}</td>
                  <td className="px-3 py-2 border-b border-sidebar" style={{ width: 100 }}>
                    {barCell(d.cpuPercent)}
                  </td>
                  <td className="px-3 py-2 border-b border-sidebar" style={{ width: 100 }}>
                    {barCell(d.memoryPercent)}
                  </td>
                  <td className="px-3 py-2 border-b border-sidebar" style={{ width: 100 }}>
                    {barCell(d.diskPercent)}
                  </td>
                  <td className="px-3 py-2 border-b border-sidebar text-right">
                    {!d.isOnline && (
                      <button
                        title="Supprimer cet agent hors-ligne"
                        onClick={async (e) => {
                          e.stopPropagation();
                          const name = d.label || d.computerName;
                          if (
                            !confirm(
                              `Supprimer définitivement l'agent "${name}" et toutes ses données (inventaire, alertes, historique CVE, heartbeats) ?\n\nCette action est irréversible.`
                            )
                          )
                            return;
                          const ok = await api.del('/devices/' + d.id);
                          if (!ok) {
                            alert('Échec de la suppression — vérifier les permissions ou les logs serveur.');
                            return;
                          }
                          setDevices((prev) => prev.filter((x) => x.id !== d.id));
                        }}
                        className="px-2.5 py-1 rounded-md border border-red-950 bg-transparent text-crit text-[11px] cursor-pointer hover:bg-red-950/30 inline-flex items-center"
                      >
                        <X size={12} strokeWidth={2} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
